import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { Send, Shield, TrendingUp, Monitor } from 'lucide-react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
      toast.success('Giriş başarılı!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Giriş başarısız');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary/20 via-background to-background p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-30" />

        <div className="relative z-10">
          <h1 className="font-heading text-4xl font-bold text-primary">ANG Dijital</h1>
          <p className="text-muted-foreground mt-2">Enterprise Email Altyapı Platformu</p>
        </div>

        <div className="relative z-10 space-y-8">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
              <Send className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-lg">Multi-Domain Email Gönderimi</h3>
              <p className="text-muted-foreground text-sm">SendGrid tabanlı küresel round-robin motor, 300K+ email/gün kapasitesi</p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
              <Shield className="w-6 h-6 text-emerald-500" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-lg">Email Temizleme & Doğrulama</h3>
              <p className="text-muted-foreground text-sm">1.5M+ email işleme kapasitesi, typo düzeltme, suppression koruması</p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
              <TrendingUp className="w-6 h-6 text-cyan-500" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-lg">Domain Warmup & Reputasyon</h3>
              <p className="text-muted-foreground text-sm">Otomatik ısınma planları, deliverability koruması ve inbox placement izleme</p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="p-3 rounded-lg bg-violet-500/10 border border-violet-500/20">
              <Monitor className="w-6 h-6 text-violet-500" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-lg">Production Monitoring</h3>
              <p className="text-muted-foreground text-sm">Gerçek zamanlı alert sistemi, domain karantina ve ops center yönetimi</p>
            </div>
          </div>
        </div>

        <p className="relative z-10 text-muted-foreground text-sm">
          © 2026 ANG Dijital. Tüm hakları saklıdır.
        </p>
      </div>

      {/* Right side - Login form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <Card className="w-full max-w-md border-border/50 bg-card/50 backdrop-blur">
          <CardHeader className="text-center">
            <div className="lg:hidden mb-4">
              <h1 className="font-heading text-3xl font-bold text-primary">ANG Dijital</h1>
            </div>
            <CardTitle className="font-heading text-2xl">Hoş Geldiniz</CardTitle>
            <CardDescription>Hesabınıza giriş yapın</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">E-posta</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="ornek@firma.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Şifre</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full glow-primary"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-muted-foreground text-sm">
                Hesabınız yok mu?{' '}
                <Link to="/register" className="text-primary hover:underline" data-testid="register-link">
                  Kayıt olun
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
